<!--
 * Path: packages/frontend/src/core/components/forms/cairoMatrix/CairoMatrixEdit.vue
 * Task: Displays the CAIRO responsibility matrix in edit mode using a native Vuetify v-table.
 * Update: Made layout fluid (100% width) to match other form components like Timeline, removing hardcoded excessive min-widths.
-->
<template>
  <div :class="{ 'mb-6': !hideHeader }">
    <SubHeader v-if="!hideHeader" :title="field.name || 'Responsibility Matrix'" :info="field.info" @click:info="$emit('show-info')" />

    <v-card variant="outlined" class="mt-2 bg-surface">
      <div class="matrix-scroll-container custom-scrollbar">
        <v-table hover class="cairo-table">
          <thead>
          <tr>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 140px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-key</v-icon>Acc.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 140px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-hard-hat</v-icon>Resp.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 140px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-star</v-icon>Cons.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 140px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-eye</v-icon>Inf.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 140px;">
              <v-icon size="small" class="me-1 text-error d-none d-sm-inline">mdi-account-cancel</v-icon>Omit.
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="pa-1" style="vertical-align: top;">
              <Multiselect :field="getFieldDef('accountableIds')" class="mb-0 mx-0 w-100" />
            </td>
            <td class="pa-1" style="vertical-align: top;">
              <Multiselect :field="getFieldDef('responsibleIds')" class="mb-0 mx-0 w-100" />
            </td>
            <td class="pa-1" style="vertical-align: top;">
              <Multiselect :field="getFieldDef('consultedIds')" class="mb-0 mx-0 w-100" />
            </td>
            <td class="pa-1" style="vertical-align: top;">
              <Multiselect :field="getFieldDef('informedIds')" class="mb-0 mx-0 w-100" />
            </td>
            <td class="pa-1" style="vertical-align: top;">
              <Multiselect :field="getFieldDef('omittedIds')" class="mb-0 mx-0 w-100" />
            </td>
          </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import Multiselect from '@/core/components/forms/Multiselect.vue';
import SubHeader from '@/core/components/forms/SubHeader.vue';

interface FieldProps {
  id?: string;
  name?: string;
  info?: string;
  type?: string;
  entitySource?: string;
}

const props = withDefaults(defineProps<{
  field: FieldProps;
  hideHeader?: boolean;
}>(), {
  hideHeader: false
});

defineEmits<{ (e: 'show-info'): void; }>();

const inverseMap: Record<string, string> = {
  accountableIds: 'accountableForartifactIds',
  responsibleIds: 'responsibleForartifactIds',
  consultedIds: 'consultedForartifactIds',
  informedIds: 'informedForartifactIds',
  omittedIds: 'omittedFromartifactIds'
};

const getFieldDef = (raciType: string) => {
  const isInverse = props.field.type === 'inverse_relation';
  const actualKey = isInverse ? inverseMap[raciType] : raciType;

  return {
    id: `raci.${actualKey}`,
    // Name wird leer gelassen, da der Table Header bereits diese Information trägt
    // und wir im v-select kein fliegendes Label benötigen
    name: '',
    entitySource: props.field.entitySource || (isInverse ? 'artifacts' : 'roles'),
    hideIcon: true // Spart Platz im Table-Design
  };
};
</script>

<style scoped>
/* Ensure the table attempts to fit within its container
   but maintains a basic minimum layout integrity */
.cairo-table {
  width: 100%;
}
.cairo-table :deep(table) {
  min-width: 650px; /* Reduced to allow fitting in typical modals, allows multiselects to shrink */
  table-layout: fixed;
}

.matrix-scroll-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.custom-scrollbar::-webkit-scrollbar {
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.15);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-on-surface), 0.3);
}

/* Specific overrides for Multiselect inside the table */
:deep(.v-select .v-field__input) {
  padding-left: 8px !important;
  padding-right: 8px !important;
  min-height: 36px !important;
}
:deep(.v-chip) {
  margin: 2px !important;
}
</style>
